/**
 * Done. / ChiroCPD — useCapStatus Hook
 *
 * Reads from CapStatusContext. Throws if used outside CapStatusProvider.
 * Exposes cap data plus convenience health-status helpers.
 */

import { useCapStatusContext } from '@/contexts/CapStatusContext';
import type { CapStatus } from '@/lib/types';
import { hourCapHealth, aiCapHealth } from '@/lib/utils';

export interface CapStatusHookResult {
  cap: CapStatus | null;
  loading: boolean;
  error: string | null;
  refreshCapStatus: () => Promise<void>;
  updateFromResponse: (cap: CapStatus) => void;
  /** Derived: health for annual hours */
  hourHealth: 'healthy' | 'warning' | 'exhausted' | null;
  /** Derived: health for AI generation cap */
  aiHealth: 'healthy' | 'warning' | 'exhausted' | null;
  /** Derived: AI generations remaining */
  aiRemaining: number | null;
  /** Derived: annual hours remaining */
  hoursRemaining: number | null;
}

export function useCapStatus(): CapStatusHookResult {
  const ctx = useCapStatusContext();
  const { cap } = ctx;

  return {
    ...ctx,
    hourHealth: cap ? hourCapHealth(cap) : null,
    aiHealth: cap ? aiCapHealth(cap) : null,
    aiRemaining: cap ? Math.max(0, cap.aiGenerationCap - cap.aiGenerationsUsed) : null,
    hoursRemaining: cap ? Math.max(0, cap.annualHourCap - cap.annualHoursLogged) : null,
  };
}
